import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowLeft, Check, Radio, Briefcase, Users } from 'lucide-react';

/**
 * TutorialWizard
 *
 * A short product walkthrough shown once onboarding finishes, and re-openable
 * from Profile. Three narrated clips, one per core feature.
 *
 * Videos autoplay muted because browsers block autoplay with sound, but the
 * recordings DO have narration — `controls` is left on so anyone who wants the
 * explanation can unmute rather than watching a silent screen capture.
 */

const STEPS = [
    {
        id: 'hiring-signals',
        icon: Radio,
        kicker: 'HIRING SIGNALS',
        title: 'Discover Hidden Opportunities',
        body: 'Uncover the hidden job market by identifying which companies are about to hire — before jobs go public.',
        video: '/demos/hiring-signals',
    },
    {
        id: 'ats-jobs',
        icon: Briefcase,
        kicker: 'LIVE JOBS',
        title: 'Be the first to know',
        body: 'We catch jobs as companies post them internally, so you are the first to know about new opportunities before they hit the major job boards.',
        video: '/demos/ats-jobs',
    },
    {
        id: 'recruiter-lookup',
        icon: Users,
        kicker: 'WARM INTROS',
        title: '4x your odds with the right introduction',
        body: 'Uncover the recruiter or hiring manager, find your warmest way in, and send outreach that opens doors instead of getting ignored.',
        video: '/demos/recruiter-lookup',
    },
];

const TutorialWizard = ({ onClose, onFinish }) => {
    const [index, setIndex] = useState(0);
    const videoRef = useRef(null);
    const step = STEPS[index];
    const isLast = index === STEPS.length - 1;

    // Restart playback on each step. Without this the <video> keeps the previous
    // clip's position when React reuses the element across steps.
    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        el.currentTime = 0;
        // Autoplay can reject (low-power mode, user settings). That is fine —
        // the poster frame stays up and the play control still works.
        el.play().catch(() => {});
    }, [index]);

    const close = () => {
        if (typeof onClose === 'function') onClose();
    };

    const next = () => {
        if (isLast) {
            if (typeof onFinish === 'function') onFinish();
            close();
            return;
        }
        setIndex((i) => i + 1);
    };

    const back = () => setIndex((i) => Math.max(0, i - 1));

    // Escape closes, arrows navigate — a modal that traps you is worse than one
    // that is easy to leave and re-open from Profile.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') close();
            if (e.key === 'ArrowRight') next();
            if (e.key === 'ArrowLeft') back();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    const Icon = step.icon;

    const content = (
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={close}
                className="fixed inset-0 z-[99998] backdrop-blur-md"
                style={{ background: 'radial-gradient(120% 90% at 50% 0%, rgba(63,87,224,.12), rgba(15,23,42,.34) 100%)' }}
            />

            <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 pointer-events-none font-dmsans">
                <motion.div
                    initial={{ opacity: 0, scale: 0.96, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 20 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="relative w-[760px] max-w-full max-h-[92vh] overflow-y-auto bg-white rounded-[24px] border border-[#E6E8EE] pointer-events-auto"
                    style={{ boxShadow: '0 30px 80px rgba(15,23,42,.22)' }}
                >
                    <button
                        onClick={close}
                        aria-label="Close tutorial"
                        className="absolute right-4 top-4 z-10 grid h-8 w-8 place-items-center rounded-full bg-white/90 text-[#64748B] transition-colors hover:bg-[#F4F5F8] hover:text-[#0F172A]"
                    >
                        <X size={16} />
                    </button>

                    <div className="relative w-full bg-[#0F172A]" style={{ aspectRatio: '16 / 9' }}>
                        <video
                            ref={videoRef}
                            key={step.id}
                            src={`${step.video}.mp4`}
                            poster={`${step.video}.jpg`}
                            autoPlay
                            muted
                            playsInline
                            controls
                            preload="metadata"
                            aria-label={`${step.title} walkthrough`}
                            className="absolute inset-0 h-full w-full object-contain"
                        />
                    </div>

                    <div className="px-8 pt-6 pb-7">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={step.id}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -8 }}
                                transition={{ duration: 0.22 }}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="grid h-6 w-6 place-items-center rounded-md bg-[#EEF1FE] text-[#3F57E0]">
                                        <Icon size={13} />
                                    </span>
                                    <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#3F57E0]">
                                        {step.kicker}
                                    </span>
                                </div>
                                <h2 className="mt-3 text-[21px] font-bold tracking-[-0.01em] text-[#0F172A]">
                                    {step.title}
                                </h2>
                                <p className="mt-2 text-[14px] leading-relaxed text-[#475569]">
                                    {step.body}
                                </p>
                            </motion.div>
                        </AnimatePresence>

                        <div className="mt-7 flex items-center justify-between gap-4">
                            <div className="flex items-center gap-1.5" role="tablist" aria-label="Tutorial progress">
                                {STEPS.map((s, i) => (
                                    <button
                                        key={s.id}
                                        onClick={() => setIndex(i)}
                                        aria-label={`Go to ${s.title}`}
                                        aria-selected={i === index}
                                        role="tab"
                                        className="h-1.5 rounded-full transition-all duration-300"
                                        style={{
                                            width: i === index ? 22 : 8,
                                            backgroundColor: i === index ? '#3F57E0' : '#D8DCE6',
                                        }}
                                    />
                                ))}
                                <span className="ml-2 text-[12px] text-[#94A3B8]">
                                    {index + 1} of {STEPS.length}
                                </span>
                            </div>

                            <div className="flex items-center gap-2">
                                {index > 0 && (
                                    <button
                                        onClick={back}
                                        className="inline-flex items-center gap-1.5 rounded-lg border border-[#E6E8EE] bg-white px-3.5 py-2 text-[13px] font-medium text-[#334155] transition-colors hover:bg-[#F4F5F8]"
                                    >
                                        <ArrowLeft size={14} />
                                        Back
                                    </button>
                                )}
                                <button
                                    onClick={close}
                                    className="rounded-lg px-3 py-2 text-[13px] font-medium text-[#64748B] transition-colors hover:text-[#0F172A]"
                                >
                                    Skip
                                </button>
                                <button
                                    onClick={next}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#3F57E0] px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-[#3348C4]"
                                >
                                    {isLast ? 'Get started' : 'Next'}
                                    {isLast ? <Check size={14} /> : <ArrowRight size={14} />}
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </>
    );

    return createPortal(content, document.body);
};

export { STEPS as TUTORIAL_STEPS };
export default TutorialWizard;
